import React, { useState, useCallback } from "react";
import { Row, Col, Card, Alert } from "react-bootstrap";
import ConfirmationModal from "@enerex/unified-ui-react/ConfirmationModal";
import { Link } from "react-router-dom";
import { rpost } from "../utils/requests";
import { DEV_URL } from "../utils/requests";
import FileUploader from "components/UnifiedFileUploader";

const CONTRACT_PARSE_API_KEY = "24c2934403bae46c64c38c53171b75dbf8849f31b77a8631";
const CONTRACT_PARSE_API_PATTERN = /\/api\/contracts\/v[12]\/parse(?:$|[/?#])/i;

const getUploadApiConfig = (url) =>
  CONTRACT_PARSE_API_PATTERN.test(url || "")
    ? {
      headers: {
        "x-api-key": CONTRACT_PARSE_API_KEY,
      },
    }
    : {};

const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
};

/**
 * Reusable upload-files modal with drag-and-drop area.
 *
 * Props:
 *  - show        : boolean – controls visibility
 *  - onHide      : () => void – called when modal should close
 *  - onUpload    : (response) => void – called after successful upload with API response
 *  - apiUrl      : string – the full URL to POST files to
 *  - webhookUrl  : string – optional webhook URL sent alongside files
 *  - title       : string (default "Upload Files")
 *  - accept      : file type map, e.g. { 'application/pdf': ['.pdf'] }
 */
const UploadFileModal = ({
  show,
  onHide,
  onUpload,
  apiUrl,
  webhookUrl = "",
  title = "Upload Files",
  type = "",
  accept,
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const handleFilesChange = useCallback((files) => {
    setSelectedFiles(files);
    setError(null);
  }, []);

  const handleRejectedFiles = useCallback(() => {
    setError("One or more selected files are not supported.");
  }, []);

  const handleUpload = async () => {
    if (!apiUrl || selectedFiles.length === 0) return;

    setUploading(true);
    setError(null);
    let uploadFailed = false;

    try {
      let uploadSucceeded = true;

      for (const file of selectedFiles) {
        // Send each file individually to the AI service
        const formData = new FormData();
        formData.append("files", file);
        formData.append("webhook", webhookUrl || "");

        const response = await rpost(apiUrl, formData, getUploadApiConfig(apiUrl));

        if (response?.status === 200) {
          // Save the AI response along with the uploaded file to backend
          const aiData = response.data;
          const saveFormData = new FormData();
          saveFormData.append("FlowId", aiData.flow.id || aiData.FlowId || "");
          saveFormData.append("Name", aiData.flow.name || aiData.Name || "");
          saveFormData.append("Status", aiData.flow.status || aiData.Status || "");
          saveFormData.append("Message", aiData.message || aiData.Message || "");
          saveFormData.append("DataType", type);
          saveFormData.append("Files", file);

          const saveResponse = await rpost(`${DEV_URL}/api/DataAI/Save`, saveFormData);
          if (!saveResponse || saveResponse.status < 200 || saveResponse.status >= 300) {
            uploadSucceeded = false;
            setError(
              saveResponse?.data?.message ||
              `Upload parsed but failed to save ${file.name}.`
            );
            break;
          }
        } else {
          uploadSucceeded = false;
          uploadFailed = true;

          setError(
            response?.data?.message ||
            `Upload failed for ${file.name} (status ${response?.status || "unknown"}).`
          );
          break;
        }
      }

     
      if (uploadSucceeded) {
        if (onUpload) onUpload();
        setSelectedFiles([]);
        onHide();
      }
    } catch (e) {
      setError(e?.message || "An unexpected error occurred during upload.");
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    if (uploading) return; // prevent closing while uploading
    setSelectedFiles([]);
    setError(null);
    onHide();
  };

  const renderSelectedFile = (file, removeFile) => (
    <Card className="mt-1 mb-0 shadow-none border">
      <div className="p-2">
        <Row className="align-items-center">
          {file.preview ? (
            <Col className="col-auto">
              <img
                className="avatar-sm rounded bg-light"
                alt={file.name}
                src={file.preview}
              />
            </Col>
          ) : (
            <Col className="col-auto">
              <div className="avatar-sm">
                <span className="avatar-title bg-primary rounded">
                  {file.name.split(".").pop().toUpperCase()}
                </span>
              </div>
            </Col>
          )}
          <Col className="ps-0">
            <Link to="#" className="text-muted fw-bold">
              {file.name}
            </Link>
            <p className="mb-0">
              <strong>{file.formattedSize || formatBytes(file.size)}</strong>
            </p>
          </Col>
          <Col className="text-end">
            {!uploading && (
              <Link
                to="#"
                className="btn btn-link btn-lg text-muted shadow-none"
                onClick={(event) => {
                  event.preventDefault();
                  removeFile();
                }}
              >
                <i className="dripicons-cross"></i>
              </Link>
            )}
          </Col>
        </Row>
      </div>
    </Card>
  );

  return (
    <ConfirmationModal
      dialogClassName="modal-md"
      show={show}
      onHide={handleClose}
      centered
      size="lg"
      title={title}
      backdrop={uploading ? "static" : true}
      confirmButtonLabel={`Upload${selectedFiles.length > 0 ? ` (${selectedFiles.length})` : ""}`}
      isLoading={uploading}
      onConfirm={() => {
        if (selectedFiles.length === 0 || uploading) return;
        handleUpload();
      }}
    >
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <FileUploader
          value={selectedFiles}
          onChange={handleFilesChange}
          onRejected={handleRejectedFiles}
          accept={accept}
          multiple
          disabled={uploading}
          customFileComponent={renderSelectedFile}
          customPlaceholder={
            <>
              <h5>Drop files here or click to browse</h5>
              <p className="text-muted mb-0">
                Selected files will appear below.
              </p>
            </>
          }
        />
    </ConfirmationModal>
  );
};

export default UploadFileModal;
